import { Loader, useModal } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { useDataRefresh, ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import _ from 'lodash';
import React, { useCallback, useContext, useState, useRef, useEffect } from 'react';
import { TranslatorContext } from '@jutro/locale';
import { ContactService } from 'twia-capability-quoteandbind-residential';
import PremiumFinanceDetails from '../PremiumFinanceDetails/PremiumFinanceDetails.jsx';
import messages from './PremiumFinanceComponent.messages.js';
import metadata from './PremiumFinanceComponent.metadata.json5';
import styles from '../TWIAAdditionalInterestComponent.module.scss';

import { Button } from '@jutro/legacy/components';

function PremiumFinanceComponent(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const { submissionVM, updateWizardData, showErrors, findInvalidComponent, onValidate } = props;
    const { authHeader } = useAuthentication();
    const [isLoading, setIsLoading] = useState(false); // DE18345
    const viewModelService = useContext(ViewModelServiceContext);
    const [additionalInfoVM, setAdditionalInfoVM] = useState();
    const [showAdditionalInterest, setShowAdditionalInterest] = useState(false);
    const { showAlert, showConfirm } = useModal();
    const mailingAddressTypeCode = viewModelService.productMetadata.get('pc').types.getTypelist('typekey.AddressType').getCode('mailingExt');
    const usCountryCode = viewModelService.productMetadata.get('pc').types.getTypelist('typekey.Country').getCode('US');
    const policyContactDetailsDTO = 'edge.capabilities.gateway.contact.dto.PolicyContactDetailsDTO';
    const { refreshData } = useDataRefresh();
    const [additionalInterest, setAdditionalInterest] = useState([]);
    const additionalInterestData = useRef(additionalInterest);
    const translator = useContext(TranslatorContext);
    const isPremFinanceAvailable = _.get(submissionVM, 'isPremiumFinanceAvailable', false)

    useEffect(() => {
        additionalInterestData.current = _.filter(submissionVM.baseData.policyContactDetails_TWIA?.value,
            pcd => pcd.policyContactRoles[0].subtype === 'PolicyAddlInterest' &&
                pcd.policyContactRoles[0].policyAdditionalInterest.cpAdditionalInterestDetails_TWIA[0].buildingInterest === false);

        if (additionalInterestData.current.length > 0) {
            _.set(submissionVM, 'isPremiumFinanceAvailable', true);
        }
    }, [])

    const addAdditionalInterest = useCallback(() => {

        const newContact = viewModelService.create({}, 'pc', 'edge.capabilities.gateway.contact.dto.PolicyContactDetailsDTO')

        newContact.active = true;
        newContact.primaryAddress.value = {};
        newContact.primaryAddress.addressType.value = mailingAddressTypeCode
        newContact.primaryAddress.country.value = usCountryCode;
        newContact.primaryAddress.mustStandardize_Ext.value = false;

        const pcr = { subtype: 'PolicyAddlInterest' };

        pcr.accountContactRole = { subtype: 'AdditionalInterest' };

        const additionalInterestDetail = { buildingInterest: false, subtype: 'CPAddlInterestDetail_TWIA', filterType: 'CPAddlInterestDetail_TWIA' };

        additionalInterestDetail.contactType = 'company';
        newContact.subtype = 'Company';
        additionalInterestDetail.type = 'other';

        pcr.policyAdditionalInterest = { cpAdditionalInterestDetails_TWIA: [additionalInterestDetail] };
        newContact.cpAdditionalInterestDetails = additionalInterestDetail;
        newContact.policyContactRoles.value = [pcr];
        setAdditionalInfoVM(() => newContact)
        setShowAdditionalInterest(true)
        // OnNext function will not call again if the changes are not available to the model so setting dummy tag.
        _.set(submissionVM, 'isEditingAdditionalInterest', true)
        updateWizardData(submissionVM)
    }, [])

    const oldVMData = [];
    const savePremiumFinance = async (updatedData) => {
        const newItem = [...additionalInterestData.current, updatedData.value];

        await saveAdditionalInterest()
        updateWizardData(submissionVM);
        setShowAdditionalInterest(false);
    };

    const cancelPremiumFinance = () => {
        setAdditionalInfoVM(oldVMData);
        setShowAdditionalInterest(false);
    };

    const getJobID = (job) => job.quoteID ? job.quoteID.value : job.jobID.value

    const saveAdditionalInterest = async () => {
        setIsLoading(true);

        await ContactService.createPolicyContact(getJobID(submissionVM), additionalInfoVM.value, authHeader).then((newContact) => {

            if (newContact) {
                const contactDetailsDTO = viewModelService.create(newContact, 'pc', policyContactDetailsDTO);
                const policyContactDetails = submissionVM.baseData.policyContactDetails_TWIA;

                if (policyContactDetails.value == null) {

                    policyContactDetails.value = {};
                }

                policyContactDetails.value.push(contactDetailsDTO.value);
                additionalInterestData.current.push(contactDetailsDTO.value);
                setShowAdditionalInterest(true);
                _.set(submissionVM, 'isPremiumFinanceAvailable', true);
                updateWizardData(submissionVM)
                setIsLoading(false);
            }
        })
            .catch(() => {
                showAlert({
                    title: messages.saveFailedTitle,
                    message: messages.saveFailedMsg,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            });

    }


    const deleteAdditionalInterest = async (aiPublicID) => {
        setIsLoading(true);

        await ContactService.removePolicyContact(getJobID(submissionVM), aiPublicID, authHeader).then(() => {

            const policyContactDetails = submissionVM.baseData.policyContactDetails_TWIA;

            if (policyContactDetails.value != null) {

                policyContactDetails.value = _.filter(policyContactDetails.value, pcd => pcd.publicID !== aiPublicID);
                additionalInterestData.current = additionalInterestData.current.filter((ai) => ai.publicID !== aiPublicID);
                setAdditionalInterest(additionalInterestData.current)
            }

            setIsLoading(false);
        })
            .catch(() => {
                showAlert({
                    title: messages.deleteFailedTitle,
                    message: messages.saveFailedMsg,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            });

    }

    const deleteRow = async (item) => {

        showConfirm({
            title: messages.removeAdditionalInterest,
            message: messages.removeAddlInterestMessage,
            status: 'warning',
            icon: 'mi-error-outline',
            confirmButtonText: commonMessages.ok,
            cancelButtonText: messages.cancel,
            className: styles.jut__Button__filled,
        }).then(async (results) => {
            setIsLoading(true);

            if (results === 'cancel' || results === 'close') {
                return _.noop();
            }

            const aiPublicID = item.publicID;

            await deleteAdditionalInterest(aiPublicID);
            _.set(submissionVM, 'isPremiumFinanceAvailable', false);
            updateWizardData(submissionVM);
            setIsLoading(false);

            return true;
        }, _.noop)
    };

    const formatInterestType = (type) => {

        switch (type) {
            case 'PREMFINANCE_TWIA': return translator(messages.premiumFinance);
            default: return type;
        }
    }

    const getName = (item) => item.contactName;

    const getInterestType = (item) => {
        if (!item && !item.policyContactRoles[0].policyAdditionalInterest.cpAdditionalInterestDetails_TWIA[0].type) {
            return undefined;
        }

        return formatInterestType(item.policyContactRoles[0].policyAdditionalInterest.cpAdditionalInterestDetails_TWIA[0].type);
    }

    const getLoanNumber = (item) => item.policyContactRoles[0].policyAdditionalInterest.cpAdditionalInterestDetails_TWIA[0].contractNumber;

    const actionButtons = (item) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
                className={styles.buttons}
                type="text"
                icon="gw-delete"
                onClick={() => deleteRow(item)}
                aria-label="Delete Row"
            />
        </div>
    );

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        addPremiumFinance: {
            visible: !showAdditionalInterest && !isPremFinanceAvailable
        },
        additionalInterestSection: {
            visible: showAdditionalInterest && !isPremFinanceAvailable,
            item: additionalInfoVM,
            saveData: savePremiumFinance,
            cancelData: cancelPremiumFinance,
            isInvalid: findInvalidComponent,
            showErrors,
            onValidate
        },
        additionalInterestTable: {
            data: additionalInterestData.current
        },

    };

    const writeValue = useCallback(
        (value, path) => {
            _.set(submissionVM, `${path}.value`, value);
            updateWizardData(submissionVM);
            refreshData();
        },
        [submissionVM, refreshData, updateWizardData]
    );

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: { premiumFinanceDetailsComponent: PremiumFinanceDetails },
        resolveCallbackMap: {
            onAdd: addAdditionalInterest,
            getName,
            getInterestType,
            getLoanNumber,
            actionButtons
        },
    };


    return (
        <Loader loaded={!isLoading}>
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={submissionVM}
            overrideProps={overrideProps}
            onModelChange={updateWizardData}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
            onValueChange={writeValue}
        />
    </Loader>
    );
}

export default PremiumFinanceComponent;
